import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Progress,
  Skeleton,
} from '@org/ui';
import { cn } from '@org/ui';
import { useTeacherId } from '../../../components/teacher-picker';

export const Route = createFileRoute('/classes/$classId/homework')({
  component: ClassHomeworkRoute,
  validateSearch: (search) => ({
    eventId: typeof search.eventId === 'string' ? search.eventId : '',
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

function ClassHomeworkRoute() {
  const { classId } = Route.useParams();
  const { eventId } = Route.useSearch();
  const teacherId = useTeacherId();
  const { data, isLoading } = trpc.teacher.events.homeworkSummary.useQuery(
    { teacherId, eventId },
    { enabled: Boolean(eventId) },
  );

  if (!eventId) {
    return (
      <Card className="border border-dashed border-rose-200 bg-white/80">
        <CardHeader>
          <CardTitle>Selecione um evento</CardTitle>
          <CardDescription>
            Abra esta página através da lista de Homework para carregar a turma associada ao evento.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (isLoading || !data) {
    return <HomeworkSkeleton />;
  }

  if (!data || !data.students.length) {
    return (
      <Card className="border border-dashed border-rose-200 bg-white/80">
        <CardHeader>
          <CardTitle>Nenhuma aluna encontrada</CardTitle>
          <CardDescription>Conecte uma turma ou adicione alunas ao evento para acompanhar o homework.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="flex w-full flex-col gap-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">Acompanhamento</p>
          <h1 className="text-3xl font-semibold text-gray-900">Homework da turma</h1>
          <p className="text-sm text-gray-600">
            {data.class.name ?? 'Classe'} • {data.event.title ?? 'Evento sem título'} •{' '}
            {data.event.startTime ? new Date(data.event.startTime).toLocaleString() : 'Sem data'}
          </p>
        </div>
        <div className="rounded-2xl border border-rose-50 bg-white px-5 py-3 text-sm text-gray-600 shadow-sm">
          <p className="font-semibold text-gray-900">{data.class.name ?? 'Classe'}</p>
          <p className="text-xs text-gray-500">
            {data.class.totalBlocks} blocos • {data.class.totalExercises} exercícios
          </p>
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.students.map((student) => (
          <StudentHomeworkCard
            key={student.studentId}
            eventId={data.event.eventId}
            classId={classId}
            blocks={data.class.blocks}
            student={student}
          />
        ))}
      </div>
    </div>
  );
}

function StudentHomeworkCard({
  eventId,
  classId,
  student,
  blocks,
}: {
  eventId: string;
  classId: string;
  student: {
    studentId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    blockProgress: { totalBlocks: number; completedBlockIds: string[]; completedCount: number };
    homeworkProgress: { submittedExercises: number; totalExercises: number; completionPercent: number };
  };
  blocks: Array<{ blockId: string; name: string | null; order: number }>;
}) {
  const fullName = `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || student.email || 'Aluna';

  return (
    <Card className="border border-rose-50 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-gray-900">{fullName}</CardTitle>
            <CardDescription className="text-xs text-gray-500">{student.email ?? '—'}</CardDescription>
          </div>
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
            {student.blockProgress.completedCount}/{student.blockProgress.totalBlocks} blocos
          </Badge>
        </div>
        <BlockProgressDots blocks={blocks} completedIds={student.blockProgress.completedBlockIds} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-gray-500">
            <span>Homework progress</span>
            <span className="text-gray-900">{student.homeworkProgress.completionPercent}%</span>
          </div>
          <Progress value={student.homeworkProgress.completionPercent} />
          <p className="text-xs text-gray-500">
            {student.homeworkProgress.submittedExercises}/{student.homeworkProgress.totalExercises} exercícios enviados
          </p>
        </div>
        <Button asChild className="w-full rounded-full bg-[#cf2e2e] text-white hover:bg-[#b82424]">
          <Link to="/classes/$classId/student/$studentId" params={{ classId, studentId: student.studentId }}>
            Ver exercícios
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function BlockProgressDots({
  blocks,
  completedIds,
}: {
  blocks: Array<{ blockId: string }>;
  completedIds: string[];
}) {
  const completedSet = new Set(completedIds);

  if (!blocks.length) {
    return <p className="text-xs text-gray-500">Nenhum bloco nesta classe.</p>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {blocks.map((block) => {
        const isCompleted = completedSet.has(block.blockId);
        return (
          <span
            key={block.blockId}
            className={cn(
              'h-3 w-3 rounded-full border transition',
              isCompleted ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300 bg-gray-200',
            )}
          />
        );
      })}
    </div>
  );
}

function HomeworkSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-40" />
        </div>
        <Skeleton className="h-16 w-56 rounded-2xl" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={`homework-skeleton-${index}`} className="border border-rose-50">
            <CardHeader className="space-y-3">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-full" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

